import { useState, useRef, useEffect } from "react";
import { type Topic, type Subtopic, type Module, type Subject } from "@/data/subjects";
import { useSubjects } from "@/contexts/SubjectsContext";
import { Upload, FileText, CheckCircle, AlertCircle, ChevronDown, Trash2, Edit2, X, Save, Loader2, Plus, PenLine, LogOut, Sparkles, Database } from "lucide-react";
import { account } from "@/lib/appwrite";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
import "katex/dist/katex.min.css";

async function uploadFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const { jwt } = await account.createJWT();
  return fetch(url, {
    ...options,
    headers: { ...(options.headers ?? {}), Authorization: `Bearer ${jwt}` },
  });
}

// ── College Board PDF → Generate + Save pipeline ─────────────────────────────

const CB_SUBJECTS = [
  { id: "sat", label: "SAT" },
  { id: "act", label: "ACT" },
];

interface ProgressState {
  phase: "idle" | "extracting" | "generating" | "done" | "error";
  statusMsg: string;
  extracted: number;
  saved: number;
  total: number;
  failed: number;
  batch: number;
  totalBatches: number;
  rateLimitWait: number;
  questions: any[];
  errorMsg: string;
  errorDetails: string[];
  metadata: any[] | null; // held for retry without re-upload
}

const INIT: ProgressState = {
  phase: "idle", statusMsg: "", extracted: 0, saved: 0, total: 0, failed: 0,
  batch: 0, totalBatches: 0, rateLimitWait: 0, questions: [], errorMsg: "",
  errorDetails: [], metadata: null,
};

const CB_PROG_KEY = "scholar_cbpdf_prog";
const CB_SUBJECT_KEY = "scholar_cbpdf_subject";

function loadProgFromStorage(): ProgressState {
  try {
    const raw = localStorage.getItem(CB_PROG_KEY);
    if (!raw) return INIT;
    const saved: ProgressState = JSON.parse(raw);
    // If it was mid-run when the page closed, surface it as interrupted so
    // the Retry button appears (metadata is still there).
    if (saved.phase === "extracting" || saved.phase === "generating") {
      return {
        ...saved,
        phase: "error",
        errorMsg: "Session was interrupted (page refresh). Use \"Retry Generation\" to continue without re-uploading.",
        rateLimitWait: 0,
      };
    }
    return saved;
  } catch { return INIT; }
}

function CBPDFGenerator() {
  const [open, setOpen] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [subject, setSubject] = useState<string>(() => localStorage.getItem(CB_SUBJECT_KEY) ?? "sat");
  const [prog, setProg] = useState<ProgressState>(loadProgFromStorage);
  const fileRef = useRef<HTMLInputElement>(null);
  const esRef = useRef<{ abort: () => void } | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  // Persist prog to localStorage whenever it changes
  useEffect(() => {
    try { localStorage.setItem(CB_PROG_KEY, JSON.stringify(prog)); } catch {}
  }, [prog]);

  // Persist subject selection
  useEffect(() => {
    try { localStorage.setItem(CB_SUBJECT_KEY, subject); } catch {}
  }, [subject]);

  // Countdown timer for rate-limit waits
  useEffect(() => {
    if (prog.rateLimitWait <= 0) return;
    const t = setInterval(() => {
      setProg(p => p.rateLimitWait > 0 ? { ...p, rateLimitWait: p.rateLimitWait - 1 } : p);
    }, 1000);
    return () => clearInterval(t);
  }, [prog.rateLimitWait]);

  async function streamSSE(res: Response) {
    if (!res.body) return;
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    esRef.current = { abort: () => reader.cancel() };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const ev = JSON.parse(line.slice(6));
          switch (ev.type) {
            case "status":
              setProg(p => ({ ...p, phase: "extracting", statusMsg: ev.message }));
              break;
            case "extracted":
              setProg(p => ({
                ...p, phase: "generating", extracted: ev.count, total: ev.count,
                totalBatches: Math.ceil(ev.count / 5),
                metadata: ev.metadata ?? p.metadata,
                statusMsg: `Extracted ${ev.count} questions. Generating in batches…`,
              }));
              break;
            case "rateLimit":
              setProg(p => ({ ...p, rateLimitWait: ev.waitSecs, statusMsg: `Rate limited — waiting ${ev.waitSecs}s…` }));
              break;
            case "batchStart":
              setProg(p => ({ ...p, batch: ev.batch, statusMsg: `Batch ${ev.batch} of ${ev.of} — generating ${ev.count} questions…` }));
              break;
            case "batchDone":
              setProg(p => ({ ...p, saved: ev.saved, failed: ev.failed ?? p.failed, statusMsg: `Saved ${ev.saved} / ${ev.total} so far…` }));
              break;
            case "warning":
              setProg(p => ({ ...p, errorDetails: [...p.errorDetails, `⚠ ${ev.message}`] }));
              break;
            case "saveError":
              setProg(p => ({ ...p, errorDetails: [...p.errorDetails, ev.message] }));
              break;
            case "done":
              setProg(p => ({ ...p, phase: "done", saved: ev.saved, failed: ev.failed, questions: ev.questions ?? [], statusMsg: `Done — ${ev.saved} questions saved.` }));
              break;
            case "error":
              setProg(p => ({ ...p, phase: "error", errorMsg: ev.message }));
              break;
          }
        } catch {}
      }
    }
  }

  async function handleGenerate() {
    if (!file) return;
    esRef.current?.abort();
    setProg({ ...INIT, phase: "extracting", statusMsg: "Uploading PDF…" });

    const form = new FormData();
    form.append("pdf", file);
    form.append("subject", subject);

    let res: Response;
    try {
      res = await uploadFetch("/api/admin/generate-from-pdf", { method: "POST", body: form });
    } catch (e: any) {
      setProg(p => ({ ...p, phase: "error", errorMsg: `Network error: ${e?.message ?? "Failed to connect to server"}` }));
      return;
    }
    if (!res.ok || !res.body) {
      const data = await res.json().catch(() => ({}));
      setProg(p => ({ ...p, phase: "error", errorMsg: data.error ?? `Server error ${res.status}` }));
      return;
    }
    await streamSSE(res);
  }

  async function handleRetry() {
    if (!prog.metadata?.length) return;
    esRef.current?.abort();
    setProg(p => ({ ...INIT, phase: "generating", extracted: p.metadata!.length, total: p.metadata!.length,
      totalBatches: Math.ceil(p.metadata!.length / 5), metadata: p.metadata,
      statusMsg: "Retrying generation with cached metadata…" }));

    const res = await uploadFetch("/api/admin/generate-from-metadata", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ metadata: prog.metadata, subject }),
    });
    if (!res.ok || !res.body) {
      const data = await res.json().catch(() => ({}));
      setProg(p => ({ ...p, phase: "error", errorMsg: data.error ?? "Request failed" }));
      return;
    }
    await streamSSE(res);
  }

  const busy = prog.phase === "extracting" || prog.phase === "generating";

  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full px-6 py-4 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
      >
        <Sparkles className="w-5 h-5 text-violet-600 shrink-0" />
        <div className="flex-1">
          <h2 className="font-semibold text-gray-800">Generate from College Board PDF</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Upload a CB question-bank PDF — Scholar reads the Domain / Skill / Difficulty headers and generates 3 new original questions per source question, saved directly to the database.
          </p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-6 pb-6 border-t pt-5 space-y-5">

          {/* Subject + file row */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-40 shrink-0">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Subject</label>
              <select
                value={subject} onChange={e => setSubject(e.target.value)}
                disabled={busy}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50"
              >
                {CB_SUBJECTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>

            <div className="flex-1">
              <label className="text-xs font-medium text-gray-500 mb-1 block">College Board PDF</label>
              <div
                onClick={() => !busy && fileRef.current?.click()}
                className={`flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-3 transition-colors ${
                  busy ? "opacity-50 cursor-not-allowed border-gray-200"
                       : "cursor-pointer hover:border-violet-400 hover:bg-violet-50 border-gray-200"
                }`}
              >
                <Upload className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-sm text-gray-500 truncate">{file ? file.name : "Click to choose a PDF…"}</span>
                {file && !busy && (
                  <button onClick={e => { e.stopPropagation(); setFile(null); setProg(INIT); try { localStorage.removeItem(CB_PROG_KEY); } catch {} }}
                    className="ml-auto text-gray-400 hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept=".pdf" className="hidden"
                onChange={e => { setFile(e.target.files?.[0] ?? null); setProg(INIT); }} />
            </div>
          </div>

          {/* Generate + Retry buttons */}
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={handleGenerate}
              disabled={!file || busy}
              className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</> : <><Sparkles className="w-4 h-4" /> Generate + Save to Database</>}
            </button>
            {prog.metadata && !busy && (
              <button
                onClick={handleRetry}
                className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors"
              >
                <Loader2 className="w-4 h-4" /> Retry Generation (no re-upload)
              </button>
            )}
          </div>

          {/* Live progress */}
          {busy && (
            <div className="space-y-3">
              {/* Status line */}
              <div className="rounded-lg bg-violet-50 border border-violet-200 px-4 py-3 text-sm text-violet-700 space-y-2">
                <div className="flex items-center gap-2 font-medium">
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                  {prog.statusMsg}
                </div>

                {/* Batch progress bar */}
                {prog.phase === "generating" && prog.totalBatches > 0 && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-violet-500">
                      <span>Batch {prog.batch} of {prog.totalBatches}</span>
                      <span>{prog.saved} saved</span>
                    </div>
                    <div className="w-full bg-violet-100 rounded-full h-1.5">
                      <div
                        className="bg-violet-500 h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${Math.round((prog.batch / prog.totalBatches) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Rate limit countdown */}
                {prog.rateLimitWait > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1">
                    <span>⏳ Rate limit — resuming in {prog.rateLimitWait}s</span>
                  </div>
                )}
              </div>

              {/* Error details (save errors / parse warnings) */}
              {prog.errorDetails.length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  <button onClick={() => setShowErrors(v => !v)}
                    className="w-full px-3 py-2 text-left flex items-center justify-between font-medium">
                    <span>{prog.errorDetails.length} error(s) — click to {showErrors ? "hide" : "show"}</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showErrors ? "rotate-180" : ""}`} />
                  </button>
                  {showErrors && (
                    <div className="border-t border-amber-200 px-3 py-2 space-y-1 max-h-40 overflow-y-auto">
                      {prog.errorDetails.map((e, i) => <p key={i} className="font-mono">{e}</p>)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {prog.phase === "error" && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600 flex gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{prog.errorMsg}</span>
            </div>
          )}

          {/* Done */}
          {prog.phase === "done" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
                <Database className="w-4 h-4 shrink-0" />
                <span><strong>{prog.saved}</strong> questions generated and saved.{prog.failed > 0 ? ` (${prog.failed} failed)` : ""}</span>
              </div>
              {prog.errorDetails.length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  <button onClick={() => setShowErrors(v => !v)}
                    className="w-full px-3 py-2 text-left flex items-center justify-between font-medium">
                    <span>{prog.errorDetails.length} save error(s) — click to {showErrors ? "hide" : "show"}</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showErrors ? "rotate-180" : ""}`} />
                  </button>
                  {showErrors && (
                    <div className="border-t border-amber-200 px-3 py-2 space-y-1 max-h-40 overflow-y-auto font-mono">
                      {prog.errorDetails.map((e, i) => <p key={i}>{e}</p>)}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-4 max-h-[700px] overflow-y-auto pr-1">
                {prog.questions.map((q, i) => (
                  <div key={i} className="rounded-lg border bg-white text-sm overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border-b">
                      <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                      <span className="text-xs font-medium text-green-700">Q{i + 1} saved</span>
                      <span className="ml-auto text-xs text-gray-400">
                        {q.topicId}{q.subtopicId ? ` · ${q.subtopicId}` : ""} · {q.difficulty}
                      </span>
                    </div>

                    {/* Side-by-side comparison */}
                    <div className="grid grid-cols-2 divide-x">
                      {/* Original */}
                      <div className="px-4 py-3 space-y-2 bg-amber-50">
                        <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Original (source)</p>
                        <p className="text-gray-700 leading-snug">{q.source?.question || "(not available)"}</p>
                        {q.source?.correctAnswer && (
                          <p className="text-xs text-amber-700 font-medium">Answer: {q.source.correctAnswer}</p>
                        )}
                        {q.source?.rationale && (
                          <p className="text-xs text-gray-500 italic">{q.source.rationale}</p>
                        )}
                      </div>

                      {/* Generated */}
                      <div className="px-4 py-3 space-y-2">
                        <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Generated</p>
                        <p className="text-gray-800 leading-snug">{q.question ?? "(no question)"}</p>
                        {q.options?.length > 0 && (
                          <div className="space-y-1 pt-1">
                            {q.options.map((opt: any) => (
                              <p key={opt.id} className={`text-xs ${opt.isCorrect ? "text-green-700 font-semibold" : "text-gray-500"}`}>
                                {opt.id}. {opt.text}{opt.isCorrect ? " ✓" : ""}
                              </p>
                            ))}
                          </div>
                        )}
                        {q.explanation && (
                          <div className="border-t pt-2">
                            <MarkdownRenderer content={q.explanation} className="text-xs text-gray-500 italic" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface Option {
  id: string;
  text: string;
  isCorrect: boolean;
}

interface Question {
  id: number;
  question: string;
  options: Option[];
  explanation: string;
  category: string;
  difficulty: "easy" | "medium" | "hard";
  image?: string;
}

interface FileStatus {
  file: File;
  status: "pending" | "parsing" | "done" | "error";
  count?: number;
  error?: string;
}

const DIFFICULTIES = ["easy", "medium", "hard"] as const;

// ── Inline "New ___" input ───────────────────────────────────────────────────
function NewItemInput({
  placeholder,
  saving,
  onSave,
  onCancel,
}: {
  placeholder: string;
  saving: boolean;
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState("");
  return (
    <div className="flex gap-2">
      <input
        autoFocus
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter") onSave(value);
          if (e.key === "Escape") onCancel();
        }}
        className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <button
        onClick={() => onSave(value)}
        disabled={!value.trim() || saving}
        className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        Add
      </button>
      <button onClick={onCancel} className="px-2 py-2 text-gray-400 hover:text-gray-600">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function toSlug(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

const PORTAL_QUESTIONS_KEY = "scholar_upload_questions";
const PORTAL_SELECTION_KEY = "scholar_upload_selection";
const PORTAL_FILE_STATUSES_KEY = "scholar_upload_file_statuses";

interface SerializedFileStatus { name: string; status: FileStatus["status"]; count?: number; error?: string; }

function loadPortalState() {
  try {
    const q = localStorage.getItem(PORTAL_QUESTIONS_KEY);
    const s = localStorage.getItem(PORTAL_SELECTION_KEY);
    const fs = localStorage.getItem(PORTAL_FILE_STATUSES_KEY);
    return {
      questions: q ? (JSON.parse(q) as Question[]) : [],
      selection: s ? JSON.parse(s) : null,
      fileStatuses: fs ? (JSON.parse(fs) as SerializedFileStatus[]) : [],
    };
  } catch { return { questions: [], selection: null, fileStatuses: [] }; }
}

// ── Main component ───────────────────────────────────────────────────────────
export function PDFUploadPortal() {
  const { subjects: dynamicSubjects } = useSubjects();
  const restored = loadPortalState();

  const [isDragging, setIsDragging] = useState(false);
  // File objects can't be serialized; restore as display-only status rows (no actual File ref)
  const [fileStatuses, setFileStatuses] = useState<FileStatus[]>(
    restored.fileStatuses.map(fs => ({ file: new File([], fs.name), status: fs.status, count: fs.count, error: fs.error }))
  );
  const [questions, setQuestions] = useState<Question[]>(restored.questions);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Persist questions whenever they change
  useEffect(() => {
    try { localStorage.setItem(PORTAL_QUESTIONS_KEY, JSON.stringify(questions)); } catch {}
  }, [questions]);

  // Persist file statuses (serializable subset only)
  useEffect(() => {
    try {
      const serializable: SerializedFileStatus[] = fileStatuses.map(fs => ({
        name: fs.file.name, status: fs.status, count: fs.count, error: fs.error,
      }));
      localStorage.setItem(PORTAL_FILE_STATUSES_KEY, JSON.stringify(serializable));
    } catch {}
  }, [fileStatuses]);

  // ── Selection state ──────────────────────────────────────────────────────
  const [selectedSubjectId, setSelectedSubjectId] = useState(restored.selection?.subjectId ?? "");
  const [selectedModuleId, setSelectedModuleId] = useState(restored.selection?.moduleId ?? "");
  const [selectedTopicId, setSelectedTopicId] = useState(restored.selection?.topicId ?? "");
  const [globalDifficulty, setGlobalDifficulty] = useState<"easy" | "medium" | "hard">(restored.selection?.difficulty ?? "medium");

  // Persist selection whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(PORTAL_SELECTION_KEY, JSON.stringify({
        subjectId: selectedSubjectId, moduleId: selectedModuleId,
        topicId: selectedTopicId, difficulty: globalDifficulty,
      }));
    } catch {}
  }, [selectedSubjectId, selectedModuleId, selectedTopicId, globalDifficulty]);

  // ── Extra (runtime-added) data ───────────────────────────────────────────
  const [extraSubjects, setExtraSubjects] = useState<Subject[]>([]);
  const [extraModules, setExtraModules] = useState<Record<string, Module[]>>({}); // subjectId -> modules
  const [extraTopics, setExtraTopics] = useState<Record<string, Topic[]>>({}); // subjectId_moduleId -> topics
  const [extraSubtopics, setExtraSubtopics] = useState<Record<string, Subtopic[]>>({}); // subjectId_moduleId_topicId -> subtopics

  // ── "Show new ___" flags ─────────────────────────────────────────────────
  const [showNewSubject, setShowNewSubject] = useState(false);
  const [showNewModule, setShowNewModule] = useState(false);
  const [showNewTopic, setShowNewTopic] = useState(false);
  const [showNewSubtopic, setShowNewSubtopic] = useState(false);
  const [savingNew, setSavingNew] = useState<"subject" | "module" | "topic" | "subtopic" | null>(null);

  // ── Subtopic selection (optional — empty string = no subtopic) ────────────
  const [selectedSubtopicId, setSelectedSubtopicId] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Derived lists ────────────────────────────────────────────────────────
  const allSubjects: Subject[] = [...dynamicSubjects, ...extraSubjects];
  const selectedSubject = allSubjects.find(s => s.id === selectedSubjectId);
  const builtInModules = selectedSubject?.modules ?? [];
  const allModules: Module[] = [...builtInModules, ...(extraModules[selectedSubjectId] ?? [])];
  const selectedModule = allModules.find(m => m.id === selectedModuleId);
  const moduleKey = `${selectedSubjectId}_${selectedModuleId}`;
  const allTopics: Topic[] = [...(selectedModule?.topics ?? []), ...(extraTopics[moduleKey] ?? [])];
  const selectedTopic = allTopics.find(t => t.id === selectedTopicId) ?? { name: selectedTopicId, subtopics: [] };

  const topicKey = `${selectedSubjectId}_${selectedModuleId}_${selectedTopicId}`;
  const allSubtopics: Subtopic[] = [...(selectedTopic.subtopics ?? []), ...(extraSubtopics[topicKey] ?? [])];
  const selectedSubtopic = allSubtopics.find(s => s.id === selectedSubtopicId);

  const targetFilename = selectedSubtopicId
    ? `${selectedSubjectId}_${selectedModuleId}_${selectedTopicId}_${selectedSubtopicId}`
    : `${selectedSubjectId}_${selectedModuleId}_${selectedTopicId}`;
  const globalCategory = selectedSubtopic?.name ?? selectedTopic.name;
  const isParsing = fileStatuses.some(f => f.status === "parsing");

  // ── Handlers ─────────────────────────────────────────────────────────────
  function handleSubjectChange(id: string) {
    setSelectedSubjectId(id);
    const subj = allSubjects.find(s => s.id === id);
    const allMods = [...(subj?.modules ?? []), ...(extraModules[id] ?? [])];
    const firstMod = allMods[0];
    if (firstMod) {
      setSelectedModuleId(firstMod.id);
      const firstTopic = [...(firstMod.topics ?? []), ...(extraTopics[`${id}_${firstMod.id}`] ?? [])][0];
      setSelectedTopicId(firstTopic?.id ?? "");
    } else {
      setSelectedModuleId("");
      setSelectedTopicId("");
    }
    setShowNewSubject(false);
    setShowNewModule(false);
    setShowNewTopic(false);
  }

  function handleModuleChange(id: string) {
    setSelectedModuleId(id);
    const mod = allModules.find(m => m.id === id);
    const topics = [...(mod?.topics ?? []), ...(extraTopics[`${selectedSubjectId}_${id}`] ?? [])];
    setSelectedTopicId(topics[0]?.id ?? "");
    setSelectedSubtopicId("");
    setShowNewModule(false);
    setShowNewTopic(false);
    setShowNewSubtopic(false);
  }

  function handleTopicChange(id: string) {
    setSelectedTopicId(id);
    setSelectedSubtopicId("");
    setShowNewSubtopic(false);
  }

  async function handleAddSubject(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    const id = toSlug(trimmed);
    if (allSubjects.find(s => s.id === id)) { handleSubjectChange(id); setShowNewSubject(false); return; }
    setSavingNew("subject");
    try {
      const res = await uploadFetch("/api/add-subject", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subjectId: id, subjectTitle: trimmed }),
      });
      const data = await res.json();
      if (data.error) { alert("Could not save subject: " + data.error); return; }
      const newSubject: Subject = {
        id, title: trimmed, description: "", color: "bg-gray-500",
        gradient: "from-gray-500 to-slate-500", modules: [],
      };
      setExtraSubjects(prev => [...prev, newSubject]);
      setSelectedSubjectId(id);
      setSelectedModuleId("");
      setSelectedTopicId("");
      setShowNewSubject(false);
    } finally {
      setSavingNew(null);
    }
  }

  async function handleAddModule(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    const id = toSlug(trimmed);
    if (allModules.find(m => m.id === id)) { handleModuleChange(id); setShowNewModule(false); return; }
    setSavingNew("module");
    try {
      const res = await uploadFetch("/api/add-module", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subjectId: selectedSubjectId, moduleId: id, moduleTitle: trimmed }),
      });
      const data = await res.json();
      if (data.error) { alert("Could not save module: " + data.error); return; }
      const newModule: Module = { id, title: trimmed, icon: FileText, topics: [] };
      setExtraModules(prev => ({ ...prev, [selectedSubjectId]: [...(prev[selectedSubjectId] ?? []), newModule] }));
      setSelectedModuleId(id);
      setSelectedTopicId("");
      setShowNewModule(false);
    } finally {
      setSavingNew(null);
    }
  }

  async function handleAddSubtopic(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    const id = toSlug(trimmed);
    if (allSubtopics.find(s => s.id === id)) { setSelectedSubtopicId(id); setShowNewSubtopic(false); return; }
    setSavingNew("subtopic");
    try {
      const res = await uploadFetch("/api/add-subtopic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subjectId: selectedSubjectId, moduleId: selectedModuleId, topicId: selectedTopicId, subtopicId: id, subtopicName: trimmed }),
      });
      const data = await res.json();
      if (data.error) { alert("Could not save subtopic: " + data.error); return; }
      setExtraSubtopics(prev => ({ ...prev, [topicKey]: [...(prev[topicKey] ?? []), { id, name: trimmed, questionCount: 0 }] }));
      setSelectedSubtopicId(id);
      setShowNewSubtopic(false);
    } finally {
      setSavingNew(null);
    }
  }

  async function handleAddTopic(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    const id = toSlug(trimmed);
    if (allTopics.find(t => t.id === id)) { setSelectedTopicId(id); setShowNewTopic(false); return; }
    setSavingNew("topic");
    try {
      const res = await uploadFetch("/api/add-topic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subjectId: selectedSubjectId, moduleId: selectedModuleId, topicId: id, topicName: trimmed }),
      });
      const data = await res.json();
      if (data.error) { alert("Could not save topic: " + data.error); return; }
      setExtraTopics(prev => ({ ...prev, [moduleKey]: [...(prev[moduleKey] ?? []), { id, name: trimmed, questionCount: 0 }] }));
      setSelectedTopicId(id);
      setShowNewTopic(false);
    } finally {
      setSavingNew(null);
    }
  }

  async function parseFile(file: File, index: number): Promise<Question[]> {
    const formData = new FormData();
    formData.append("pdf", file);
    setFileStatuses(prev => prev.map((f, i) => i === index ? { ...f, status: "parsing" } : f));
    try {
      const res = await uploadFetch("/api/parse-pdf", { method: "POST", body: formData });
      const data = await res.json();
      if (data.error) {
        setFileStatuses(prev => prev.map((f, i) => i === index ? { ...f, status: "error", error: data.error } : f));
        return [];
      }
      const qs: Question[] = (data.questions ?? []).map((q: Question) => ({
        ...q,
        category: globalCategory || q.category,
        difficulty: globalDifficulty,
      }));
      const warningMsg = data.warnings?.length ? ` (⚠️ ${data.warnings.length} chunk(s) failed)` : "";
      setFileStatuses(prev => prev.map((f, i) => i === index
        ? { ...f, status: "done", count: qs.length, error: warningMsg || undefined }
        : f
      ));
      return qs;
    } catch (e: any) {
      setFileStatuses(prev => prev.map((f, i) => i === index ? { ...f, status: "error", error: e.message } : f));
      return [];
    }
  }

  async function handleFilesSelected(files: File[]) {
    const pdfs = files.filter(f => f.name.endsWith(".pdf"));
    const nonPdfs = files.filter(f => !f.name.endsWith(".pdf"));
    if (nonPdfs.length) setError(`Skipped ${nonPdfs.length} non-PDF file(s).`);
    else setError(null);
    if (!pdfs.length) return;
    setSaveStatus(null);
    const existingNames = new Set(fileStatuses.map(f => f.file.name));
    const newFiles = pdfs.filter(f => !existingNames.has(f.name));
    if (!newFiles.length) return;
    const startIndex = fileStatuses.length;
    setFileStatuses(prev => [...prev, ...newFiles.map(f => ({ file: f, status: "pending" as const }))]);
    const allNew: Question[] = [];
    for (let i = 0; i < newFiles.length; i++) {
      const qs = await parseFile(newFiles[i]!, startIndex + i);
      allNew.push(...qs);
    }
    setQuestions(prev => [...prev, ...allNew]);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    handleFilesSelected(Array.from(e.dataTransfer.files));
  }

  function removeFile(index: number) { setFileStatuses(prev => prev.filter((_, i) => i !== index)); }
  function clearAll() {
    setFileStatuses([]); setQuestions([]); setError(null); setSaveStatus(null);
    try { localStorage.removeItem(PORTAL_QUESTIONS_KEY); localStorage.removeItem(PORTAL_FILE_STATUSES_KEY); } catch {}
  }

  function applyGlobalSettings() {
    setQuestions(prev => prev.map(q => ({ ...q, category: globalCategory || q.category, difficulty: globalDifficulty })));
  }

  function updateQuestion(idx: number, updated: Question) {
    setQuestions(prev => prev.map((q, i) => i === idx ? updated : q));
  }
  function deleteQuestion(idx: number) { setQuestions(prev => prev.filter((_, i) => i !== idx)); }

  async function saveQuestions() {
    if (!questions.length) return;
    setIsSaving(true);
    setSaveStatus(null);
    try {
      const res = await uploadFetch("/api/save-questions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          questions,
          subjectId: selectedSubjectId,
          moduleId: selectedModuleId,
          topicId: selectedTopicId,
          subtopicId: selectedSubtopicId || undefined,
          difficulty: globalDifficulty,
        }),
      });
      const data = await res.json();
      if (data.error) { setSaveStatus({ success: false, message: data.error }); return; }
      setSaveStatus({ success: true, message: `✅ Saved ${data.added} questions to database` });
      setQuestions([]);
      setFileStatuses([]);
      try { localStorage.removeItem(PORTAL_QUESTIONS_KEY); localStorage.removeItem(PORTAL_FILE_STATUSES_KEY); } catch {}
    } catch (e: any) {
      setSaveStatus({ success: false, message: e.message });
    } finally {
      setIsSaving(false);
    }
  }

  // ── Reusable dropdown+new row ────────────────────────────────────────────
  function SelectRow({
    label, value, options, onChange,
    showNew, onToggleNew, onAdd, saving,
    newPlaceholder, disabled,
  }: {
    label: React.ReactNode;
    value: string;
    options: { id: string; label: string }[];
    onChange: (id: string) => void;
    showNew: boolean;
    onToggleNew: () => void;
    onAdd: (name: string) => void;
    saving: boolean;
    newPlaceholder: string;
    disabled?: boolean;
  }) {
    return (
      <div>
        <label className="text-sm text-gray-600 mb-1 block">{label}</label>
        {showNew ? (
          <NewItemInput
            placeholder={newPlaceholder}
            saving={saving}
            onSave={onAdd}
            onCancel={onToggleNew}
          />
        ) : (
          <div className="flex gap-2">
            <div className="relative flex-1">
              <select
                value={value}
                onChange={e => onChange(e.target.value)}
                disabled={disabled}
                className="w-full appearance-none border rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:opacity-50"
              >
                {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                {options.length === 0 && <option value="">— none —</option>}
              </select>
              <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            <button
              onClick={onToggleNew}
              className="px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 flex items-center gap-1 whitespace-nowrap"
            >
              <Plus className="w-3.5 h-3.5" /> New
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center gap-3">
        <button onClick={() => window.history.back()} className="text-gray-500 hover:text-gray-800 text-sm">← Back</button>
        <div className="h-4 w-px bg-gray-300" />
        <FileText className="w-5 h-5 text-blue-600" />
        <h1 className="text-lg font-semibold text-gray-900">Admin Upload Portal</h1>
        <div className="flex-1" />
        <a
          href="/admin/review-questions"
          className="text-sm text-violet-600 hover:text-violet-800 font-medium px-3 py-1.5 rounded-lg hover:bg-violet-50 transition-colors"
        >
          Review Questions →
        </a>
        <button
          onClick={async () => { await account.deleteSession("current").catch(() => {}); window.location.href = "/"; }}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">

        {/* ── College Board PDF Generator ─────────────────────────────── */}
        <CBPDFGenerator />

        {/* Step 1: Target */}
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h2 className="font-semibold text-gray-800">1. Choose Target Subject, Module &amp; Topic</h2>
          <div className="grid grid-cols-2 gap-4">

            <SelectRow
              label="Subject"
              value={selectedSubjectId}
              options={allSubjects.map(s => ({ id: s.id, label: s.title }))}
              onChange={handleSubjectChange}
              showNew={showNewSubject}
              onToggleNew={() => { setShowNewSubject(v => !v); setShowNewModule(false); setShowNewTopic(false); }}
              onAdd={handleAddSubject}
              saving={savingNew === "subject"}
              newPlaceholder="e.g. AP Chemistry"
            />

            <SelectRow
              label="Module"
              value={selectedModuleId}
              options={allModules.map(m => ({ id: m.id, label: m.title }))}
              onChange={handleModuleChange}
              showNew={showNewModule}
              onToggleNew={() => { setShowNewModule(v => !v); setShowNewSubject(false); setShowNewTopic(false); }}
              onAdd={handleAddModule}
              saving={savingNew === "module"}
              newPlaceholder="e.g. Thermodynamics"
            />

            <SelectRow
              label="Topic"
              value={selectedTopicId}
              options={allTopics.map(t => ({ id: t.id, label: t.name }))}
              onChange={handleTopicChange}
              showNew={showNewTopic}
              onToggleNew={() => { setShowNewTopic(v => !v); setShowNewSubject(false); setShowNewModule(false); setShowNewSubtopic(false); }}
              onAdd={handleAddTopic}
              saving={savingNew === "topic"}
              newPlaceholder="e.g. Circular Motion"
              disabled={!selectedModuleId}
            />

            <SelectRow
              label={<span>Subtopic <span className="text-gray-400 font-normal">(optional)</span></span>}
              value={selectedSubtopicId}
              options={[{ id: "", label: "— none —" }, ...allSubtopics.map(s => ({ id: s.id, label: s.name }))]}
              onChange={setSelectedSubtopicId}
              showNew={showNewSubtopic}
              onToggleNew={() => { setShowNewSubtopic(v => !v); setShowNewSubject(false); setShowNewModule(false); setShowNewTopic(false); }}
              onAdd={handleAddSubtopic}
              saving={savingNew === "subtopic"}
              newPlaceholder="e.g. Projectile Motion"
              disabled={!selectedTopicId}
            />

            <div>
              <label className="text-sm text-gray-600 mb-1 block">Default Difficulty</label>
              <div className="relative">
                <select value={globalDifficulty} onChange={e => setGlobalDifficulty(e.target.value as any)}
                  className="w-full appearance-none border rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  {DIFFICULTIES.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {questions.length > 0 && (
            <button onClick={applyGlobalSettings} className="text-sm text-blue-600 hover:text-blue-800 underline">
              Apply category/difficulty to all {questions.length} questions
            </button>
          )}
          <p className="text-xs text-gray-400">
            Will save to database: <code className="bg-gray-100 px-1 rounded">{selectedSubjectId}/{selectedModuleId}{selectedTopicId ? `/${selectedTopicId}` : ""}{selectedSubtopicId ? `/${selectedSubtopicId}` : ""}</code>
          </p>
        </div>

        {/* Step 2a: Manual entry */}
        <ManualQuestionForm
          defaultCategory={globalCategory}
          defaultDifficulty={globalDifficulty}
          onAdd={(q: Question) => setQuestions(prev => [...prev, { ...q, id: prev.length + 1 }])}
        />

        {/* Step 2b: Upload PDFs */}
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h2 className="font-semibold text-gray-800">3. Upload PDFs</h2>

          <div
            className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${
              isDragging ? "border-blue-400 bg-blue-50" : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
            }`}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input ref={fileInputRef} type="file" accept=".pdf" multiple className="hidden"
              onChange={e => { const files = Array.from(e.target.files ?? []); if (files.length) handleFilesSelected(files); e.target.value = ""; }} />
            <Upload className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-600 font-medium">Drop multiple PDFs here or click to browse</p>
            <p className="text-sm text-gray-400 mt-1">You can add more files at any time</p>
          </div>

          {error && (
            <div className="flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {fileStatuses.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700">{fileStatuses.length} file{fileStatuses.length !== 1 ? "s" : ""}</p>
                <button onClick={clearAll} className="text-xs text-gray-400 hover:text-red-500">Clear all</button>
              </div>
              {fileStatuses.map((fs, i) => (
                <div key={i} className="flex items-center gap-3 border rounded-lg px-3 py-2.5 bg-gray-50">
                  <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="text-sm text-gray-700 flex-1 truncate">{fs.file.name}</span>
                  <span className="text-xs shrink-0">
                    {fs.status === "pending" && <span className="text-gray-400">Pending</span>}
                    {fs.status === "parsing" && (
                      <span className="flex items-center gap-1 text-blue-600">
                        <Loader2 className="w-3 h-3 animate-spin" /> Parsing…
                      </span>
                    )}
                    {fs.status === "done" && (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-3 h-3" /> {fs.count} questions{fs.error}
                      </span>
                    )}
                    {fs.status === "error" && (
                      <span className="flex items-center gap-1 text-red-500">
                        <AlertCircle className="w-3 h-3" />
                        <span className="max-w-xs truncate" title={fs.error}>{fs.error || "Error"}</span>
                      </span>
                    )}
                  </span>
                  {fs.status !== "parsing" && (
                    <button onClick={() => removeFile(i)} className="text-gray-300 hover:text-gray-500 shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Step 3: Review */}
        {questions.length > 0 && (
          <div className="bg-white rounded-xl border p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-800">3. Review &amp; Edit ({questions.length} questions)</h2>
              <button onClick={saveQuestions} disabled={isSaving || isParsing}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save to App
              </button>
            </div>

            {isParsing && (
              <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 rounded-lg p-3">
                <Loader2 className="w-4 h-4 animate-spin" />
                Still parsing remaining files — you can review parsed questions while waiting.
              </div>
            )}

            <div className="space-y-3">
              {questions.map((q, idx) => (
                <QuestionCard
                  key={idx}
                  question={q}
                  index={idx}
                  isEditing={editingIdx === idx}
                  onEdit={() => setEditingIdx(editingIdx === idx ? null : idx)}
                  onDelete={() => deleteQuestion(idx)}
                  onSave={(updated) => { updateQuestion(idx, updated); setEditingIdx(null); }}
                  onCancel={() => setEditingIdx(null)}
                />
              ))}
            </div>

            <div className="flex justify-end pt-2">
              <button onClick={saveQuestions} disabled={isSaving || isParsing}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors">
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save {questions.length} Questions to App
              </button>
            </div>
          </div>
        )}

        {saveStatus && (
          <div className={`flex items-center gap-2 rounded-xl p-4 text-sm font-medium ${
            saveStatus.success ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-700"
          }`}>
            {saveStatus.success ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            {saveStatus.message}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Manual Question Form ─────────────────────────────────────────────────────
const OPTION_IDS = ["A", "B", "C", "D"] as const;
const BLANK_OPTIONS = OPTION_IDS.map(id => ({ id, text: "", isCorrect: false }));

function ManualQuestionForm({
  defaultCategory,
  defaultDifficulty,
  onAdd,
}: {
  defaultCategory: string;
  defaultDifficulty: "easy" | "medium" | "hard";
  onAdd: (q: Question) => void;
}) {
  const [open, setOpen] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [options, setOptions] = useState(BLANK_OPTIONS.map(o => ({ ...o })));
  const [correctId, setCorrectId] = useState<string>("A");
  const [explanation, setExplanation] = useState("");
  const [category, setCategory] = useState(defaultCategory);
  const [difficulty, setDifficulty] = useState<"easy" | "medium" | "hard">(defaultDifficulty);

  // Sync defaults when parent selection changes
  useEffect(() => { setCategory(defaultCategory); }, [defaultCategory]);
  useEffect(() => { setDifficulty(defaultDifficulty); }, [defaultDifficulty]);

  function reset() {
    setQuestionText("");
    setOptions(BLANK_OPTIONS.map(o => ({ ...o })));
    setCorrectId("A");
    setExplanation("");
    setCategory(defaultCategory);
    setDifficulty(defaultDifficulty);
  }

  function handleAdd() {
    if (!questionText.trim()) return;
    if (options.some(o => !o.text.trim())) return;
    const final: Question = {
      id: 0,
      question: questionText.trim(),
      options: options.map(o => ({ ...o, isCorrect: o.id === correctId })),
      explanation: explanation.trim(),
      category,
      difficulty,
    };
    onAdd(final);
    reset();
  }

  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full px-6 py-4 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
      >
        <PenLine className="w-5 h-5 text-blue-600 shrink-0" />
        <div className="flex-1">
          <h2 className="font-semibold text-gray-800">2. Add Question Manually</h2>
          <p className="text-xs text-gray-400 mt-0.5">Type a question directly — no PDF needed</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-4 border-t pt-4">
          {/* Question text */}
          <div>
            <label className="text-sm text-gray-600 mb-1 block">Question</label>
            <textarea
              rows={3}
              value={questionText}
              onChange={e => setQuestionText(e.target.value)}
              placeholder="Enter the question text…"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Options */}
          <div>
            <label className="text-sm text-gray-600 mb-2 block">Answer Options <span className="text-gray-400">(select the correct one)</span></label>
            <div className="space-y-2">
              {options.map((opt, i) => (
                <div key={opt.id} className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="manual-correct"
                    checked={correctId === opt.id}
                    onChange={() => setCorrectId(opt.id)}
                    className="accent-blue-600 shrink-0"
                  />
                  <span className={`text-xs font-semibold w-5 ${correctId === opt.id ? "text-blue-600" : "text-gray-400"}`}>{opt.id}</span>
                  <input
                    type="text"
                    value={opt.text}
                    onChange={e => setOptions(prev => prev.map((o, j) => j === i ? { ...o, text: e.target.value } : o))}
                    placeholder={`Option ${opt.id}`}
                    className={`flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${correctId === opt.id ? "border-blue-300 bg-blue-50" : ""}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Explanation + meta */}
          <div>
            <label className="text-sm text-gray-600 mb-1 block">Explanation <span className="text-gray-400">(optional)</span></label>
            <textarea
              rows={2}
              value={explanation}
              onChange={e => setExplanation(e.target.value)}
              placeholder="Why is the answer correct?"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Category</label>
              <input
                type="text"
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Difficulty</label>
              <div className="relative">
                <select
                  value={difficulty}
                  onChange={e => setDifficulty(e.target.value as any)}
                  className="w-full appearance-none border rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {DIFFICULTIES.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              onClick={handleAdd}
              disabled={!questionText.trim() || options.some(o => !o.text.trim())}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" /> Add to Queue
            </button>
            <button
              onClick={reset}
              className="text-sm text-gray-400 hover:text-gray-600 px-3 py-2"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Question Card ────────────────────────────────────────────────────────────
function QuestionCard({ question, index, isEditing, onEdit, onDelete, onSave, onCancel }: {
  question: Question;
  index: number;
  isEditing: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onSave: (q: Question) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<Question>({ ...question });
  const handleEdit = () => { setDraft({ ...question }); onEdit(); };

  const difficultyColor = {
    easy: "bg-green-100 text-green-700",
    medium: "bg-yellow-100 text-yellow-700",
    hard: "bg-red-100 text-red-700",
  }[question.difficulty];

  if (isEditing) {
    return (
      <div className="border-2 border-blue-300 rounded-xl p-4 space-y-3 bg-blue-50">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-blue-700">Editing Q{index + 1}</span>
          <button onClick={onCancel}><X className="w-4 h-4 text-gray-400 hover:text-gray-600" /></button>
        </div>
        <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
          rows={3} value={draft.question} onChange={e => setDraft(d => ({ ...d, question: e.target.value }))} />
        <div className="space-y-1.5">
          {draft.options.map((opt, oi) => (
            <div key={opt.id} className="flex items-center gap-2">
              <input type="radio" name={`correct-${index}`} checked={opt.isCorrect}
                onChange={() => setDraft(d => ({ ...d, options: d.options.map((o, i) => ({ ...o, isCorrect: i === oi })) }))}
                className="accent-blue-600" />
              <span className="text-xs text-gray-500 w-4">{opt.id}</span>
              <input className="flex-1 border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                value={opt.text} onChange={e => setDraft(d => ({ ...d, options: d.options.map((o, i) => i === oi ? { ...o, text: e.target.value } : o) }))} />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Category</label>
            <input className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
              value={draft.category} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Difficulty</label>
            <select className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
              value={draft.difficulty} onChange={e => setDraft(d => ({ ...d, difficulty: e.target.value as any }))}>
              {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
        <textarea className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
          rows={2} placeholder="Explanation" value={draft.explanation}
          onChange={e => setDraft(d => ({ ...d, explanation: e.target.value }))} />
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="text-sm px-3 py-1.5 border rounded-lg hover:bg-gray-100">Cancel</button>
          <button onClick={() => onSave(draft)} className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save</button>
        </div>
      </div>
    );
  }

  const correctOpt = question.options.find(o => o.isCorrect);
  return (
    <div className="border rounded-xl p-4 hover:border-gray-300 transition-colors group">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="text-xs font-semibold text-gray-400">Q{index + 1}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${difficultyColor}`}>{question.difficulty}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{question.category}</span>
            {correctOpt && <span className="text-xs text-green-600 font-medium">Answer: {correctOpt.id}</span>}
          </div>
          <p className="text-sm text-gray-800 leading-snug">{question.question}</p>
          {question.image && (
            <img src={question.image} alt={`Graph for Q${index + 1}`}
              className="mt-2 max-h-48 rounded border border-gray-200 bg-white" />
          )}
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5">
            {question.options.map(opt => (
              <p key={opt.id} className={`text-xs ${opt.isCorrect ? "text-green-700 font-medium" : "text-gray-500"}`}>
                {opt.id}. {opt.text}
              </p>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button onClick={handleEdit} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600">
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-red-500">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
